import classNames from "classnames";
import React, { FC } from "react";

import { FieldLabel } from "../../../../components/newUI/text";
import { AddAutomationModal } from "../../../work/pages/automation/AddAutomationModal";
import { AddWorkAutomationSection } from "./components/AddWorkAutomationSection";
import { AddWorkFormFooter } from "./components/AddWorkFormFooter";
import { AddWorkModalHeader } from "./components/AddWorkModalHeader";
import { AddWorkRepeatSection } from "./components/AddWorkRepeatSection";
import { AddWorkSequenceSection } from "./components/AddWorkSequenceSection";
import { AssignmentSection } from "./components/AssignmentSection";
import { InvalidDatesModal } from "./components/InvalidDatesModal";
import { ScheduleBudgetSection } from "./components/ScheduleBudgetSection";
import { WorkDetailsSection } from "./components/WorkDetailsSection";
import { useAddWorkModalController } from "./hooks/useAddWorkModalController";
import { AddWorkModalProps } from "./model/addWork.types";

export const AddWorkModal: FC<AddWorkModalProps> = (props) => {
  const { hideContactSelect, isOpen, onClose } = props;
  const {
    automationLookups,
    automationPreview,
    currentUserId,
    selectedContacts,
    selectedWorkType,
    sequenceDraft,
    setSequenceDraft,
    repeatDraft,
    setRepeatDraft,
    workDates,
    control,
    createdBy,
    contactSelectTab,
    contactTagOptions,
    contactsOptions,
    errors,
    handleCloseAutomationModal,
    handleDeleteAutomationDraft,
    handleLoadMoreContacts,
    handleOpenAutomationModal,
    handleSaveAutomationDraft,
    handleSubmit,
    handleWorkTypeChange,
    hasRoleOrAssignee,
    hasMoreContacts,
    isAddingWork,
    isInvalidDatesModalOpen,
    isLoadingContactsFirstPage,
    isLoadingContactTags,
    isLoadingMoreContacts,
    isLoadingRoles,
    isLoadingTemplates,
    isLoadingUsers,
    isLoadingWorkTypeDefaults,
    isLoadingWorkTypes,
    onSubmit,
    register,
    renderRoleMembersPreview,
    roleOptions,
    searchContacts,
    searchTemplate,
    searchType,
    selectedAssigneeItems,
    selectedAutomationTarget,
    selectedContactTag,
    selectedRoleItems,
    setContactSelectTab,
    setIsInvalidDatesModalOpen,
    setSearchContacts,
    setSearchTemplate,
    setSearchType,
    setSelectedContactTag,
    submitError,
    templateOptions,
    trigger,
    usersOptions,
    workTypeOptions,
    noteDraft,
    setNoteDraft,
    showNote,
  } = useAddWorkModalController(props);

  return (
    <div
      className={classNames(
        "modal !z-[4]",
        isOpen ? "modal-open" : "modal-close",
      )}>
      <div
        className={
          "modal-box flex max-h-[calc(100vh-5em)] w-full max-w-lg flex-col overflow-hidden p-0"
        }>
        <AddWorkModalHeader onClose={onClose} />
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={"flex min-h-0 flex-1 flex-col"}>
          <div className={"min-h-0 flex-1 overflow-y-auto"}>
            <div className={"grid gap-4 px-5 pb-6 pt-7"}>
              <WorkDetailsSection
                control={control}
                errors={errors}
                register={register}
                workTypeOptions={workTypeOptions}
                templateOptions={templateOptions}
                contactsOptions={contactsOptions}
                contactTagOptions={contactTagOptions}
                contactSelectTab={contactSelectTab}
                selectedContactTag={selectedContactTag}
                searchType={searchType}
                searchTemplate={searchTemplate}
                searchContacts={searchContacts}
                setContactSelectTab={setContactSelectTab}
                setSelectedContactTag={setSelectedContactTag}
                setSearchType={setSearchType}
                setSearchTemplate={setSearchTemplate}
                setSearchContacts={setSearchContacts}
                handleLoadMoreContacts={handleLoadMoreContacts}
                hasMoreContacts={hasMoreContacts}
                handleWorkTypeChange={(newWorkType, onChange) => {
                  void handleWorkTypeChange(newWorkType, onChange);
                }}
                isLoadingWorkTypes={isLoadingWorkTypes}
                isLoadingTemplates={isLoadingTemplates}
                isLoadingContactsFirstPage={isLoadingContactsFirstPage}
                isLoadingContactTags={isLoadingContactTags}
                isLoadingMoreContacts={isLoadingMoreContacts}
                isLoadingWorkTypeDefaults={isLoadingWorkTypeDefaults}
                hideContactSelect={hideContactSelect}
              />

              <AssignmentSection
                control={control}
                errors={errors}
                usersOptions={usersOptions}
                roleOptions={roleOptions}
                selectedAssigneeItems={selectedAssigneeItems}
                selectedRoleItems={selectedRoleItems}
                isLoadingUsers={isLoadingUsers}
                isLoadingRoles={isLoadingRoles}
                isLoadingWorkTypeDefaults={isLoadingWorkTypeDefaults}
                hasRoleOrAssignee={hasRoleOrAssignee}
                trigger={trigger}
                renderRoleMembersPreview={renderRoleMembersPreview}
              />

              <ScheduleBudgetSection
                control={control}
                errors={errors}
                register={register}
              />

              {showNote && (
                <div>
                  <FieldLabel
                    label={"Note"}
                    infoTooltip={"Added to the work item's Notes after Create"}
                  />
                  <textarea
                    aria-label={"Note"}
                    value={noteDraft}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    rows={3}
                    placeholder={"Optional note for the work item"}
                    className={
                      "w-full resize-y rounded-[10px] border border-[#DCE2EA] px-3 py-2 text-sm text-[#333333] outline-none placeholder:text-[rgba(51,51,51,0.6)] focus:border-[#6B4EFF]"
                    }
                  />
                </div>
              )}

              <AddWorkRepeatSection
                draft={repeatDraft}
                onChange={setRepeatDraft}
                work={workDates}
              />

              <AddWorkAutomationSection
                automation={automationPreview}
                options={automationLookups.options}
                repeats={Boolean(repeatDraft)}
                onAddAutomation={handleOpenAutomationModal}
                onDeleteAutomation={handleDeleteAutomationDraft}
              />

              <AddWorkSequenceSection
                draft={sequenceDraft}
                onChange={setSequenceDraft}
                customerId={selectedContacts[0]?.value || undefined}
                serviceId={selectedWorkType?.value || undefined}
                recipients={selectedContacts.map((contact) =>
                  String(contact.label || ""),
                )}
                currentUserId={currentUserId}
              />
            </div>
          </div>
          <AddWorkFormFooter
            onCancel={onClose}
            submitError={submitError}
            isSubmitting={isAddingWork}
            isLoadingDefaults={isLoadingWorkTypeDefaults}
          />
        </form>
      </div>
      <AddAutomationModal
        isOpen={Boolean(selectedAutomationTarget)}
        target={selectedAutomationTarget}
        createdBy={createdBy}
        lookups={automationLookups}
        onClose={handleCloseAutomationModal}
        onSave={handleSaveAutomationDraft}
      />
      {isInvalidDatesModalOpen && (
        <InvalidDatesModal onClose={() => setIsInvalidDatesModalOpen(false)} />
      )}
    </div>
  );
};
